import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { format, parseISO, isValid } from 'date-fns';
import { poppinsBoldBase64, poppinsRegularBase64 } from '../../../../Utilities/Font';

const formatDateLong = (dateString) => {
  if (!dateString) return '';
  try {
    const d = parseISO(dateString);
    if (isValid(d)) {
      return format(d, 'MMMM dd, yyyy');
    }
    return dateString;
  } catch {
    return dateString;
  }
};

const formatTime = (timeStr) => {
  if (!timeStr || timeStr === '-' || timeStr === 'null' || timeStr === 'undefined') return '-';
  const str = String(timeStr).trim();

  // If already like "09:00 AM" or "9:00 PM"
  if (/^\d{1,2}:\d{2}(\s?[APap][Mm])$/.test(str)) {
    return str.toUpperCase();
  }

  // If like "09:00:00" or "09:00" (time only without date)
  if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(str)) {
    const [hStr, mStr] = str.split(':');
    let h = parseInt(hStr, 10);
    const m = mStr.padStart(2, '0');
    const ampm = h >= 12 ? 'PM' : 'AM';
    h = h % 12 || 12;
    return `${String(h).padStart(2, '0')}:${m} ${ampm}`;
  }

  try {
    let d = parseISO(str);
    if (!isValid(d)) {
      d = parseISO(str.replace(' ', 'T'));
    }
    if (!isValid(d)) {
      d = new Date(str);
    }
    if (isValid(d)) {
      return format(d, 'hh:mm a');
    }
    return str;
  } catch {
    return str;
  }
};

const formatDateShort = (dateString) => {
  if (!dateString) return '';
  try {
    const d = parseISO(dateString);
    if (isValid(d)) {
      return format(d, 'MM-dd-yyyy');
    }
    return dateString;
  } catch {
    return dateString;
  }
};

const mapGridDetailToRow = (item, index) => {
  const sched = Number(item.ScheduledHours ?? item.scheduledHrs ?? 0);
  const actual = Number(item.ActualHours ?? item.actualHrs ?? 0);
  const ot = Number(item.OTHours ?? item.overtimeHrs ?? 0);
  const varH = Number(item.VarianceHours ?? item.varianceHrs ?? 0);

  let otPercent = item.OTPercent ?? item.otPercent;
  if (otPercent !== undefined && otPercent !== null) {
    if (typeof otPercent === 'number') {
      otPercent = `${otPercent.toFixed(2)}%`;
    } else if (typeof otPercent === 'string' && !otPercent.includes('%')) {
      const num = Number(otPercent);
      otPercent = !isNaN(num) ? `${num.toFixed(2)}%` : otPercent;
    }
  } else {
    otPercent = actual > 0 ? `${((ot / actual) * 100).toFixed(2)}%` : '0.00%';
  }

  let otRisk = item.OTRisk || item.otRisk;
  if (!otRisk) {
    otRisk = ot > 2 ? 'High' : ot > 0 ? 'Medium' : 'Low';
  }

  let dateVal = '';
  const rawDate = item.Date || item.date || item.WeekStartDate || item.weekStartDate;
  if (rawDate) {
    dateVal = formatDateShort(rawDate);
  }

  return {
    id: `${item.EmployeeId || 'emp'}-${index}`,
    employeeId: item.EmployeeId,
    date: dateVal,
    employeeName: item.EmployeeName || '-',
    department: item.NimbleDeptName || item.DepartmentName || item.department || '-',
    jobTitle: item.NimbleJobName || item.JobName || item.jobTitle || '-',
    punchIn: formatTime(item.PunchIn || item.punchIn),
    punchOut: formatTime(item.PunchOut || item.punchOut),
    scheduledHrs: sched,
    actualHrs: actual,
    overtimeHrs: ot,
    varianceHrs: varH,
    otPercent: otPercent,
    otRisk: otRisk,
    status: item.Status || item.status || 'On Schedule',
    week: item.Week ?? item.week ?? item.WeekName,
    weekStartDate: item.WeekStartDate ?? item.weekStartDate,
    weekEndDate: item.WeekEndDate ?? item.weekEndDate,
    month: item.Month ?? item.month ?? item.MonthName,
    monthStartDate: item.MonthStartDate ?? item.monthStartDate,
    monthEndDate: item.MonthEndDate ?? item.monthEndDate,
    year: item.Year ?? item.year,
    corporationName: item.CorporationName || 'Corporation',
    pcName: item.PCName || (item.PCID ? `PC ${item.PCID}` : '-'),
    employeeCount: Number(item.EmployeeCount ?? item.employeeCount ?? 1),
  };
};

const buildTableGroupsInternal = (rawGridDetails, reportType, isMultiCorp, filters) => {
  if (!rawGridDetails || rawGridDetails.length === 0) return [];
  const mapped = rawGridDetails.map(mapGridDetailToRow);

  if (reportType === 'weekly') {
    const weeklyMap = new Map();
    mapped.forEach((rec) => {
      const weekKey = !isMultiCorp
        ? `${rec.week || 'W1'}___${rec.weekStartDate || ''}___${rec.weekEndDate || ''}`
        : `${rec.corporationName || ''}___${rec.week || 'W1'}___${rec.weekStartDate || ''}___${rec.weekEndDate || ''}`;
      if (!weeklyMap.has(weekKey)) {
        weeklyMap.set(weekKey, {
          corpName: rec.corporationName,
          weekCode: rec.week || 'W1',
          weekStartDate: rec.weekStartDate,
          weekEndDate: rec.weekEndDate,
          records: [],
        });
      }
      weeklyMap.get(weekKey).records.push(rec);
    });

    const groups = [];
    weeklyMap.forEach((val, key) => {
      let startFormatted = '';
      let endFormatted = '';
      if (val.weekStartDate) {
        try {
          const s = parseISO(val.weekStartDate);
          if (isValid(s)) startFormatted = format(s, 'MMM dd, yyyy');
        } catch { }
      }
      if (val.weekEndDate) {
        try {
          const e = parseISO(val.weekEndDate);
          if (isValid(e)) endFormatted = format(e, 'MMM dd, yyyy');
        } catch { }
      }
      const dateRangeStr = startFormatted && endFormatted ? ` (${startFormatted} – ${endFormatted})` : '';
      const headerTitle = !isMultiCorp
        ? `${val.weekCode}${dateRangeStr}`
        : `${val.corpName ? `${val.corpName} | ` : ''}${val.weekCode}${dateRangeStr}`;
      groups.push({ id: key, headerTitle, records: val.records });
    });
    return groups;
  }

  if (reportType === 'monthly') {
    const monthMap = new Map();
    mapped.forEach((rec) => {
      const monthKey = !isMultiCorp
        ? `${rec.month || 'M1'}___${rec.year || ''}___${rec.monthStartDate || ''}`
        : `${rec.corporationName || ''}___${rec.month || 'M1'}___${rec.year || ''}___${rec.monthStartDate || ''}`;
      if (!monthMap.has(monthKey)) {
        monthMap.set(monthKey, {
          corpName: rec.corporationName,
          monthCode: rec.month || 'M1',
          year: rec.year || '',
          records: [],
        });
      }
      monthMap.get(monthKey).records.push(rec);
    });

    const groups = [];
    monthMap.forEach((val, key) => {
      const rawMonth = val.monthCode;
      const yearStr = val.year ? `, ${val.year}` : '';
      const monthTitle = `${rawMonth}${yearStr}`;
      const headerTitle = !isMultiCorp
        ? monthTitle
        : `${val.corpName ? `${val.corpName} | ` : ''}${monthTitle}`;
      groups.push({ id: key, headerTitle, records: val.records });
    });
    return groups;
  }

  // Daily
  if (!isMultiCorp) {
    const dailyMap = new Map();
    mapped.forEach((rec) => {
      const key = `${rec.department}___${rec.jobTitle}`;
      if (!dailyMap.has(key)) {
        dailyMap.set(key, {
          date: rec.date || filters?.fromDate || '',
          deptName: rec.department,
          jobName: rec.jobTitle,
          records: [],
        });
      }
      dailyMap.get(key).records.push(rec);
    });

    const groups = [];
    dailyMap.forEach((val, key) => {
      groups.push({
        id: key,
        headerTitle: `${val.deptName} / ${val.jobName}`,
        deptName: val.deptName,
        jobName: val.jobName,
        records: val.records,
      });
    });
    return groups;
  }

  // Multi-Corporation Daily Mode: 1st complete 1 corporation, then start another corporation
  const corpMap = new Map();
  mapped.forEach((rec) => {
    const corpKey = rec.corporationName || 'Corporation';
    if (!corpMap.has(corpKey)) {
      corpMap.set(corpKey, new Map());
    }
    const dateKey = rec.date || filters?.fromDate || '';
    const dateMap = corpMap.get(corpKey);
    if (!dateMap.has(dateKey)) {
      dateMap.set(dateKey, {
        corpName: rec.corporationName || '',
        date: dateKey,
        records: [],
      });
    }
    dateMap.get(dateKey).records.push(rec);
  });

  const groups = [];
  corpMap.forEach((dateMap, corpKey) => {
    dateMap.forEach((val, dateKey) => {
      let formattedDate = val.date;
      if (val.date) {
        try {
          formattedDate = isValid(parseISO(val.date))
            ? format(parseISO(val.date), 'MM-dd-yyyy')
            : val.date;
        } catch {
          formattedDate = val.date;
        }
      }
      groups.push({ id: `${corpKey}___${dateKey}`, headerTitle: formattedDate, dateTitle: formattedDate, corpName: val.corpName, records: val.records });
    });
  });
  return groups;
};

const computeReportHeaderLabels = ({
  filters = {},
  reportType = 'daily',
  reportTitle = 'Time and Attendance Report',
  reportData = null,
  rawGridDetails = [],
  isMultiCorp = false,
  pcNo = '-',
}) => {
  const dataRecords =
    rawGridDetails && rawGridDetails.length > 0
      ? rawGridDetails
      : reportData?.GridDetails && Array.isArray(reportData.GridDetails)
        ? reportData.GridDetails
        : [];

  const uniqueCorpsMap = new Map();
  dataRecords.forEach((r) => {
    const name = r.CorporationName || r.corpName;
    const id = r.CorporationID || r.corpId;
    if (
      name !== null &&
      name !== undefined &&
      String(name).trim() !== '' &&
      String(name).toLowerCase() !== 'null' &&
      String(name).toLowerCase() !== 'undefined'
    ) {
      const key = String(id || name).trim();
      if (!uniqueCorpsMap.has(key)) {
        uniqueCorpsMap.set(key, String(name).trim());
      }
    } else if (
      id !== null &&
      id !== undefined &&
      String(id).trim() !== '' &&
      String(id).toLowerCase() !== 'null' &&
      String(id).toLowerCase() !== 'undefined'
    ) {
      const key = String(id).trim();
      if (!uniqueCorpsMap.has(key)) {
        uniqueCorpsMap.set(key, String(id).trim());
      }
    }
  });
  const uniqueCorps = Array.from(uniqueCorpsMap.values());

  const isMultiCorpEffective = uniqueCorps.length > 0 ? uniqueCorps.length > 1 : Boolean(isMultiCorp);

  const uniquePcsMap = new Map();
  dataRecords.forEach((r) => {
    const pcId = r.PCID ?? r.pcId ?? r.ProfitCenterID;
    const pcName = r.PCName ?? r.pcName ?? r.ProfitCenterName;
    const hasValidId =
      pcId !== null &&
      pcId !== undefined &&
      String(pcId).trim() !== '' &&
      String(pcId).toLowerCase() !== 'null' &&
      String(pcId).toLowerCase() !== 'undefined';
    const hasValidName =
      pcName !== null &&
      pcName !== undefined &&
      String(pcName).trim() !== '' &&
      String(pcName).toLowerCase() !== 'null' &&
      String(pcName).toLowerCase() !== 'undefined';
    if (hasValidName || hasValidId) {
      const key = hasValidName ? String(pcName).trim() : String(pcId).trim();
      if (!uniquePcsMap.has(key)) {
        uniquePcsMap.set(key, hasValidName ? String(pcName).trim() : String(pcId).trim());
      }
    }
  });
  const uniquePcs = Array.from(uniquePcsMap.values());

  let finalCorp = '-';
  if (uniqueCorps.length > 1) {
    finalCorp = `${uniqueCorps.length} Corporations selected`;
  } else if (uniqueCorps.length === 1) {
    finalCorp = uniqueCorps[0];
  } else if (filters?.corporation && filters.corporation !== 'all' && !filters.corporation.includes('Corporations Selected')) {
    finalCorp = filters.corporation;
  }

  let finalPc = '-';
  if (uniquePcs.length > 1) {
    finalPc = `${uniquePcs.length} Profit centers selected`;
  } else if (uniquePcs.length === 1) {
    finalPc = uniquePcs[0];
  }

  let finalPcNo = '-';
  if (uniquePcs.length === 0) {
    finalPcNo = '-';
  } else if (uniquePcs.length > 1) {
    finalPcNo = 'Multiple';
  } else if (dataRecords.length > 0) {
    const match = dataRecords.find((r) => {
      const val = r.PCNo ?? r.pcNo ?? r.PCNumber ?? r.ProfitCenterNo;
      return val !== undefined && val !== null && String(val).trim() !== '' && String(val).toLowerCase() !== 'null';
    });
    if (match) {
      finalPcNo = String(match.PCNo ?? match.pcNo ?? match.PCNumber ?? match.ProfitCenterNo).trim();
    } else if (pcNo && pcNo !== 'Multiple') {
      finalPcNo = pcNo;
    }
  }

  const uniqueBrands = [
    ...new Set(
      dataRecords
        .map((r) => r?.Brand ?? r?.brand ?? r?.BrandName ?? r?.brandName ?? r?.Business ?? r?.business ?? '')
        .map((b) => String(b).trim())
        .filter((b) => b !== '' && b.toLowerCase() !== 'null' && b.toLowerCase() !== 'undefined')
    ),
  ];
  let finalBusiness = '-';
  if (uniqueBrands.length === 1) {
    finalBusiness = uniqueBrands[0];
  } else if (uniqueBrands.length > 1) {
    finalBusiness = 'Multiple';
  }

  const fromDateLong = formatDateLong(filters?.fromDate);
  const toDateLong = formatDateLong(filters?.toDate);
  const dateRangeStr = fromDateLong && toDateLong ? `${fromDateLong} To ${toDateLong}` : (filters?.fromDate || '');

  const reportTypeFormatted =
    reportType === 'daily'
      ? 'Daily'
      : reportType === 'weekly'
        ? 'Weekly'
        : reportType === 'monthly'
          ? 'Monthly'
          : reportType.charAt(0).toUpperCase() + reportType.slice(1);

  const mainTitle = isMultiCorpEffective
    ? 'Combined Time and Attendance Report'
    : reportTitle || 'Time and Attendance Report';

  return {
    finalCorpLabel: finalCorp,
    finalPcLabel: finalPc,
    finalPcNoLabel: finalPcNo,
    finalBusinessLabel: finalBusiness,
    dateRangeStr,
    reportTypeFormatted,
    mainTitle,
    isMultiCorpEffective,
  };
};

const drawHeaderBox = (doc, boxData) => {
  const {
    isMultiCorp,
    reportTypeFormatted,
    mainTitle,
    finalCorpLabel,
    dateRangeStr,
    finalPcLabel,
    finalBusinessLabel,
    finalPcNoLabel,
    PAGE_MARGIN,
    pageWidth,
  } = boxData;

  const startY = 8;
  const rowHeight = 6.2;
  const totalBoxHeight = rowHeight * 3; // 18.6 mm
  const totalWidth = pageWidth - 2 * PAGE_MARGIN;

  const col0W = 55;
  const col1W = 85;
  const col3W = 35;
  const col4W = 95;
  const col2W = Math.max(60, totalWidth - (col0W + col1W + col3W + col4W)); // center title column

  const x0 = PAGE_MARGIN;
  const x1 = x0 + col0W;
  const x2 = x1 + col1W;
  const x3 = x2 + col2W;
  const x4 = x3 + col3W;

  const y0 = startY;
  const y1 = y0 + rowHeight;
  const y2 = y1 + rowHeight;

  doc.setLineWidth(0.15);
  doc.setDrawColor(217, 217, 217);

  const drawCell = (x, y, w, h, bgRgb, text, isBold = true, fontSize = 8, textColor = [17, 24, 39], align = 'left') => {
    doc.setFillColor(bgRgb[0], bgRgb[1], bgRgb[2]);
    doc.rect(x, y, w, h, 'FD');

    if (!text || String(text).trim() === '') return;

    doc.setFont('Poppins', isBold ? 'bold' : 'normal');
    doc.setFontSize(fontSize);
    doc.setTextColor(textColor[0], textColor[1], textColor[2]);

    const textY = y + h / 2 + 1.4;
    if (align === 'center') {
      doc.text(String(text), x + w / 2, textY, { align: 'center', maxWidth: w - 4 });
    } else {
      doc.text(String(text), x + 3, textY, { align: 'left', maxWidth: w - 6 });
    }
  };

  const grayBg = [249, 250, 251];
  const whiteBg = [255, 255, 255];
  const grayText = [55, 65, 81];
  const darkText = [17, 24, 39];

  // Row 0 Left
  drawCell(x0, y0, col0W, rowHeight, grayBg, isMultiCorp ? 'Corporation Legal Name' : 'Corporation/Legal Name', true, 7.8, grayText);
  drawCell(x1, y0, col1W, rowHeight, whiteBg, finalCorpLabel, true, 7.8, darkText);

  // Row 1 Left
  drawCell(x0, y1, col0W, rowHeight, grayBg, 'Profit Center', true, 7.8, grayText);
  drawCell(x1, y1, col1W, rowHeight, whiteBg, finalPcLabel, true, 7.8, darkText);

  // Row 2 Left (for multiple corporations, both cells are empty while row is preserved)
  const showPcNo = !isMultiCorp;
  drawCell(x0, y2, col0W, rowHeight, grayBg, showPcNo ? 'PC NO' : '', true, 7.8, grayText);
  drawCell(x1, y2, col1W, rowHeight, whiteBg, showPcNo ? finalPcNoLabel : '', true, 7.8, darkText);

  // Col 2: Center Title Box (Spanning 3 Rows)
  doc.setFillColor(255, 255, 255);
  doc.rect(x2, y0, col2W, totalBoxHeight, 'FD');
  doc.setFont('Poppins', 'bold');
  doc.setTextColor(17, 24, 39);

  if (isMultiCorp) {
    doc.setFontSize(10.5);
    doc.text('Combined Time and Attendance Report', x2 + col2W / 2, y0 + totalBoxHeight / 2 - 1.5, { align: 'center', maxWidth: col2W - 6 });
    doc.setFontSize(9.5);
    doc.text(`View by – ${reportTypeFormatted}`, x2 + col2W / 2, y0 + totalBoxHeight / 2 + 3.8, { align: 'center', maxWidth: col2W - 6 });
  } else {
    doc.setFontSize(11);
    doc.text(mainTitle, x2 + col2W / 2, y0 + totalBoxHeight / 2 + 1.4, { align: 'center', maxWidth: col2W - 6 });
  }

  // Row 0 Right
  drawCell(x3, y0, col3W, rowHeight, grayBg, 'Date', true, 7.8, grayText);
  drawCell(x4, y0, col4W, rowHeight, whiteBg, dateRangeStr, true, 7.8, darkText);

  // Row 1 Right
  drawCell(x3, y1, col3W, rowHeight, grayBg, 'Business', true, 7.8, grayText);
  drawCell(x4, y1, col4W, rowHeight, whiteBg, finalBusinessLabel, true, 7.8, darkText);

  // Row 2 Right
  drawCell(x3, y2, col3W, rowHeight, grayBg, '', true, 7.8, grayText);
  drawCell(x4, y2, col4W, rowHeight, whiteBg, '', true, 7.8, darkText);
};

/**
 * Generate PDF for Time and Attendance Report with enterprise typography, ReportHeader Box, pre-calculated values & Poppins styling
 */
export const generateTimeAndAttendancePdf = ({
  filters = {},
  reportType = 'daily',
  reportTitle = 'Time and Attendance Report',
  selectedFields = [],
  reportData = null,
  tableGroups = [],
  rawGridDetails = [],
  hasProfitCenter = false,
  isMultiCorp: isMultiCorpProp = false,
  pcNo = '-',
}) => {
  // Setup PDF Document in Landscape (A3 provides crisp, spacious columns for dense enterprise data)
  const doc = new jsPDF('l', 'mm', 'a3');

  // Register Poppins Fonts
  try {
    if (poppinsRegularBase64) {
      doc.addFileToVFS('Poppins-Regular.ttf', poppinsRegularBase64);
      doc.addFont('Poppins-Regular.ttf', 'Poppins', 'normal');
    }
    if (poppinsBoldBase64) {
      doc.addFileToVFS('Poppins-Bold.ttf', poppinsBoldBase64);
      doc.addFont('Poppins-Bold.ttf', 'Poppins', 'bold');
    }
    doc.setFont('Poppins', 'normal');
  } catch (e) {
    console.warn('Font loading fallback to default:', e);
  }

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const PAGE_MARGIN = 12;
  const PAGE_BOTTOM_MARGIN = 14;
  const DATA_TABLE_TOP_MARGIN = 32;
  let currentY = DATA_TABLE_TOP_MARGIN;

  const {
    finalCorpLabel,
    finalPcLabel,
    finalPcNoLabel,
    finalBusinessLabel,
    dateRangeStr,
    reportTypeFormatted,
    mainTitle,
    isMultiCorpEffective,
  } = computeReportHeaderLabels({
    filters,
    reportType,
    reportTitle,
    reportData,
    rawGridDetails,
    isMultiCorp: isMultiCorpProp,
    pcNo,
  });

  const isMultiCorp = isMultiCorpEffective;

  const isDaily = reportType === 'daily';
  let activeFields = Array.isArray(selectedFields) && selectedFields.length > 0
    ? [...selectedFields]
    : (!isDaily
        ? ['scheduledHrs', 'actualHrs', 'overtimeHrs', 'varianceHrs', 'otPercent', 'otRisk']
        : (isMultiCorp
            ? ['punchIn', 'punchOut', 'scheduledHrs', 'actualHrs', 'varianceOvertimeHrs', 'otPercent', 'otRisk']
            : ['punchIn', 'punchOut', 'scheduledHrs', 'actualHrs', 'overtimeHrs', 'varianceHrs', 'status']));

  if (!isDaily || !isMultiCorp) {
    if (activeFields.includes('varianceOvertimeHrs')) {
      activeFields = activeFields.filter((f) => f !== 'varianceOvertimeHrs');
      if (!activeFields.includes('overtimeHrs')) activeFields.push('overtimeHrs');
      if (!activeFields.includes('varianceHrs')) activeFields.push('varianceHrs');
    }
  }

  const dataRecords =
    rawGridDetails && rawGridDetails.length > 0
      ? rawGridDetails
      : reportData?.GridDetails && Array.isArray(reportData.GridDetails)
        ? reportData.GridDetails
        : [];

  const hasDepartment = dataRecords.some((item) => {
    const deptName = item.NimbleDeptName ?? item.DepartmentName ?? item.deptName ?? item.Department;
    return deptName !== null && deptName !== undefined && String(deptName).trim() !== '' && String(deptName).toLowerCase() !== 'null' && String(deptName).toLowerCase() !== 'undefined';
  });
  const hasJob = dataRecords.some((item) => {
    const jobName = item.NimbleJobName ?? item.JobName ?? item.jobName ?? item.JobTitle ?? item.Job;
    return jobName !== null && jobName !== undefined && String(jobName).trim() !== '' && String(jobName).toLowerCase() !== 'null' && String(jobName).toLowerCase() !== 'undefined';
  });

  const hasEmployeeDetails = dataRecords.some((r) => Boolean(r.EmployeeName || r.EmployeeId));
  const isSummaryMultiCorp = isMultiCorp && (!hasEmployeeDetails || reportType === 'weekly' || reportType === 'monthly');

  const baseHeaderStyles = {
    font: 'Poppins',
    fontStyle: 'bold',
    fontSize: 7.5,
    fillColor: [239, 239, 239], // #EFEFEF Light Gray
    textColor: [0, 0, 0],
    lineColor: [200, 200, 200], // #C8C8C8 Thin Border
    lineWidth: 0.1,
    halign: 'center',
    valign: 'middle',
    cellPadding: 3,
  };

  const baseBodyStyles = {
    font: 'Poppins',
    fontSize: 7.5,
    textColor: [34, 34, 34],
    lineColor: [224, 224, 224], // #E0E0E0
    lineWidth: 0.1,
    valign: 'middle',
    cellPadding: 2.5,
  };

  if (isSummaryMultiCorp) {
    // -------------------------------------------------------------
    // Multi-Corporation Summary View
    // -------------------------------------------------------------
    const isVarianceOtActive = isDaily && isMultiCorp && (activeFields.includes('varianceOvertimeHrs') || activeFields.includes('varianceHrs') || activeFields.includes('overtimeHrs'));
    let headers = [];
    if (isDaily) {
      headers = [
        'Corporation',
        ...(hasProfitCenter ? ['Profit Center'] : []),
        'Department',
        'Job Title',
        'Date',
        'Employee Count',
        ...(activeFields.includes('punchIn') ? ['Punch In'] : []),
        ...(activeFields.includes('punchOut') ? ['Punch Out'] : []),
        ...(activeFields.includes('scheduledHrs') ? ['Scheduled Hrs'] : []),
        ...(activeFields.includes('actualHrs') ? ['Actual Hrs'] : []),
        ...(isVarianceOtActive ? ['Variance Hours /OT Hrs'] : []),
        ...(activeFields.includes('otPercent') ? ['OT%'] : []),
        ...(activeFields.includes('otRisk') ? ['OT Risk'] : []),
      ];
    } else if (reportType === 'weekly') {
      headers = [
        'Corporation',
        ...(hasProfitCenter ? ['Profit Center'] : []),
        ...(hasDepartment ? ['Department'] : []),
        ...(hasJob ? ['Job Title'] : []),
        'Week',
        'Week End Date',
        'Employee Count',
        ...(activeFields.includes('scheduledHrs') ? ['Scheduled Hrs'] : []),
        ...(activeFields.includes('actualHrs') ? ['Actual Hrs'] : []),
        ...(activeFields.includes('overtimeHrs') ? ['Overtime Hrs'] : []),
        ...(activeFields.includes('varianceHrs') ? ['Variance Hours'] : []),
        ...(activeFields.includes('otPercent') ? ['OT%'] : []),
        ...(activeFields.includes('otRisk') ? ['OT Risk'] : []),
      ];
    } else {
      // Monthly
      headers = [
        'Corporation',
        ...(hasProfitCenter ? ['Profit Center'] : []),
        ...(hasDepartment ? ['Department'] : []),
        ...(hasJob ? ['Job Title'] : []),
        'Month',
        'Employee Count',
        ...(activeFields.includes('scheduledHrs') ? ['Scheduled Hrs'] : []),
        ...(activeFields.includes('actualHrs') ? ['Actual Hrs'] : []),
        ...(activeFields.includes('overtimeHrs') ? ['Overtime Hrs'] : []),
        ...(activeFields.includes('varianceHrs') ? ['Variance Hours'] : []),
        ...(activeFields.includes('otPercent') ? ['OT%'] : []),
        ...(activeFields.includes('otRisk') ? ['OT Risk'] : []),
      ];
    }

    const rows = [];
    const corpMap = new Map();

    dataRecords.forEach((item) => {
      const corpKey = item.CorporationID || item.CorporationName || 'Corp';
      const corpName = item.CorporationName || 'Corporation';
      const pcKey = item.PCID || item.PCName || 'All';
      const pcName = item.PCName || (item.PCID ? `PC ${item.PCID}` : '-');

      if (!corpMap.has(corpKey)) {
        corpMap.set(corpKey, { corpName, pcMap: new Map() });
      }
      const corpObj = corpMap.get(corpKey);
      if (!corpObj.pcMap.has(pcKey)) {
        corpObj.pcMap.set(pcKey, { pcName, records: [] });
      }
      corpObj.pcMap.get(pcKey).records.push(item);
    });

    let allCorpsSched = 0;
    let allCorpsActual = 0;
    let allCorpsOT = 0;
    let allCorpsVar = 0;
    let allCorpsMaxEmp = 0;

    corpMap.forEach((corpObj) => {
      let corpSched = 0;
      let corpActual = 0;
      let corpOT = 0;
      let corpVar = 0;
      let corpMaxEmp = 0;

      corpObj.pcMap.forEach((pcObj) => {
        let pcSched = 0;
        let pcActual = 0;
        let pcOT = 0;
        let pcVar = 0;
        let pcMaxEmp = 0;

        // Group by Department & Job
        const deptJobMap = new Map();
        pcObj.records.forEach((rec) => {
          const deptName = rec.NimbleDeptName || rec.DepartmentName || rec.deptName || '';
          const jobName = rec.NimbleJobName || rec.JobName || rec.jobTitle || '';
          const djKey = `${deptName}___${jobName}`;
          if (!deptJobMap.has(djKey)) {
            deptJobMap.set(djKey, { deptName, jobName, records: [] });
          }
          deptJobMap.get(djKey).records.push(rec);
        });

        deptJobMap.forEach((djGroup) => {
          const hasDj = Boolean(djGroup.deptName || djGroup.jobName);
          let djSched = 0;
          let djActual = 0;
          let djOT = 0;
          let djVar = 0;
          let djMaxEmp = 0;

          if (reportType === 'weekly') {
            djGroup.records.forEach((rec) => {
              const sched = Number(rec.ScheduledHours ?? rec.ScheduledHrs ?? rec.scheduledHrs ?? 0);
              const actual = Number(rec.ActualHours ?? rec.actualHrs ?? 0);
              const ot = Number(rec.OTHours ?? rec.overtimeHrs ?? 0);
              const varH = Number(rec.VarianceHours ?? rec.varianceHrs ?? 0);
              const empCount = Number(rec.EmployeeCount ?? rec.employeeCount ?? 1);

              djSched += sched;
              djActual += actual;
              djOT += ot;
              djVar += varH;
              if (empCount > pcMaxEmp) pcMaxEmp = empCount;

              let otPStr = '0.00%';
              if (rec.OTPercent !== undefined && rec.OTPercent !== null) {
                otPStr = `${Number(rec.OTPercent).toFixed(2)}%`;
              } else if (actual > 0) {
                otPStr = `${((ot / actual) * 100).toFixed(2)}%`;
              }

              let dateStr = '';
              const endD = rec.WeekEndDate || rec.Date || rec.WeekStartDate;
              if (endD) {
                dateStr = formatDateShort(endD);
              }

              const row = [
                corpObj.corpName,
                ...(hasProfitCenter ? [pcObj.pcName] : []),
                ...(hasDepartment ? [rec.NimbleDeptName || rec.DepartmentName || rec.deptName || '-'] : []),
                ...(hasJob ? [rec.NimbleJobName || rec.JobName || rec.jobTitle || '-'] : []),
                rec.Week || 'W01',
                dateStr,
                empCount,
                ...(activeFields.includes('scheduledHrs') ? [sched.toFixed(2)] : []),
                ...(activeFields.includes('actualHrs') ? [actual.toFixed(2)] : []),
                ...(activeFields.includes('overtimeHrs') ? [ot.toFixed(2)] : []),
                ...(activeFields.includes('varianceHrs') ? [varH.toFixed(2)] : []),
                ...(activeFields.includes('otPercent') ? [otPStr] : []),
                ...(activeFields.includes('otRisk')
                  ? [rec.OTRisk || (ot > 2 ? 'High' : ot > 0 ? 'Medium' : 'Low')]
                  : []),
              ];
              rows.push(row);
            });
          } else {
            // monthly or daily
            djGroup.records.forEach((rec) => {
              const sched = Number(rec.ScheduledHours ?? rec.ScheduledHrs ?? rec.scheduledHrs ?? 0);
              const actual = Number(rec.ActualHours ?? rec.actualHrs ?? 0);
              const ot = Number(rec.OTHours ?? rec.overtimeHrs ?? 0);
              const varH = Number(rec.VarianceHours ?? rec.varianceHrs ?? 0);
              const empCount = Number(rec.EmployeeCount ?? rec.employeeCount ?? 1);

              djSched += sched;
              djActual += actual;
              djOT += ot;
              djVar += varH;
              if (empCount > pcMaxEmp) pcMaxEmp = empCount;

              let otPStr = '0.00%';
              if (rec.OTPercent !== undefined && rec.OTPercent !== null) {
                otPStr = `${Number(rec.OTPercent).toFixed(2)}%`;
              } else if (actual > 0) {
                otPStr = `${((ot / actual) * 100).toFixed(2)}%`;
              }

              let dateStr = '';
              const endD = rec.WeekEndDate || rec.Date || rec.WeekStartDate;
              if (endD) {
                dateStr = formatDateShort(endD);
              }

              const row = [
                corpObj.corpName,
                ...(hasProfitCenter ? [pcObj.pcName] : []),
                ...(reportType === 'monthly' && hasDepartment ? [rec.NimbleDeptName || rec.DepartmentName || rec.deptName || '-'] : []),
                ...(reportType === 'monthly' && hasJob ? [rec.NimbleJobName || rec.JobName || rec.jobTitle || '-'] : []),
                ...(isDaily ? [rec.NimbleDeptName || rec.DepartmentName || rec.deptName || '-', rec.NimbleJobName || rec.JobName || rec.jobTitle || '-'] : []),
                ...(reportType === 'monthly' ? [rec.Year ? `${rec.Month || 'Jan'},${rec.Year}` : (rec.Month || 'Jan')] : []),
                ...(isDaily ? [dateStr] : []),
                empCount,
                ...(isDaily && activeFields.includes('punchIn') ? [formatTime(rec.PunchIn || rec.punchIn)] : []),
                ...(isDaily && activeFields.includes('punchOut') ? [formatTime(rec.PunchOut || rec.punchOut)] : []),
                ...(activeFields.includes('scheduledHrs') ? [sched.toFixed(2)] : []),
                ...(activeFields.includes('actualHrs') ? [actual.toFixed(2)] : []),
                ...(isVarianceOtActive ? [varH.toFixed(2)] : []),
                ...(!isDaily && activeFields.includes('overtimeHrs') ? [ot.toFixed(2)] : []),
                ...(!isDaily && activeFields.includes('varianceHrs') ? [varH.toFixed(2)] : []),
                ...(activeFields.includes('otPercent') ? [otPStr] : []),
                ...(activeFields.includes('otRisk')
                  ? [rec.OTRisk || (ot > 2 ? 'High' : ot > 0 ? 'Medium' : 'Low')]
                  : []),
              ];
              rows.push(row);
            });
          }

          if (hasDj && deptJobMap.size > 1 && reportType !== 'monthly' && reportType !== 'weekly') {
            // Department Total Row
            const djOtPercent = djActual > 0 ? `${((djOT / djActual) * 100).toFixed(2)}%` : '0.00%';
            const djOtRisk = djOT > djActual * 0.1 ? 'High' : djOT > 0 ? 'Medium' : 'Low';
            const djTotalRow = [
              corpObj.corpName,
              ...(hasProfitCenter ? [pcObj.pcName] : []),
              ...(reportType === 'monthly' && hasDepartment ? [`${djGroup.deptName || djGroup.jobName} Total`] : []),
              ...(reportType === 'monthly' && hasJob ? [''] : []),
              ...(reportType === 'weekly' && hasDepartment ? [`${djGroup.deptName || djGroup.jobName} Total`] : []),
              ...(reportType === 'weekly' && hasJob ? [''] : []),
              ...(reportType === 'weekly' ? ['', ''] : []),
              ...(reportType === 'monthly' ? [''] : []),
              ...(isDaily ? [`${djGroup.deptName || djGroup.jobName} Total`] : []),
              djMaxEmp,
              ...(isDaily && activeFields.includes('punchIn') ? ['-'] : []),
              ...(isDaily && activeFields.includes('punchOut') ? ['-'] : []),
              ...(activeFields.includes('scheduledHrs') ? [djSched.toFixed(2)] : []),
              ...(activeFields.includes('actualHrs') ? [djActual.toFixed(2)] : []),
              ...(isVarianceOtActive ? [djVar.toFixed(2)] : []),
              ...(!isDaily && activeFields.includes('overtimeHrs') ? [djOT.toFixed(2)] : []),
              ...(!isDaily && activeFields.includes('varianceHrs') ? [djVar.toFixed(2)] : []),
              ...(activeFields.includes('otPercent') ? [djOtPercent] : []),
              ...(activeFields.includes('otRisk') ? [djOtRisk] : []),
            ];
            rows.push({
              isSummary: true,
              isGrand: false,
              data: djTotalRow,
            });
          }

          pcSched += djSched;
          pcActual += djActual;
          pcOT += djOT;
          pcVar += djVar;
          if (djMaxEmp > pcMaxEmp) pcMaxEmp = djMaxEmp;
        });

        if (hasProfitCenter) {
          const pcOtPercent = pcActual > 0 ? `${((pcOT / pcActual) * 100).toFixed(2)}%` : '0.00%';
          const pcOtRisk = pcOT > pcActual * 0.1 ? 'High' : pcOT > 0 ? 'Medium' : 'Low';
          const pcTotalRow = [
            corpObj.corpName,
            `${pcObj.pcName === '-' ? corpObj.corpName : pcObj.pcName} Total`,
            ...(reportType === 'monthly' && hasDepartment ? [''] : []),
            ...(reportType === 'monthly' && hasJob ? [''] : []),
            ...(reportType === 'weekly' && hasDepartment ? [''] : []),
            ...(reportType === 'weekly' && hasJob ? [''] : []),
            ...(reportType === 'weekly' ? ['', ''] : []),
            ...(reportType === 'monthly' ? [''] : []),
            ...(isDaily ? [''] : []),
            pcMaxEmp,
            ...(isDaily && activeFields.includes('punchIn') ? ['-'] : []),
            ...(isDaily && activeFields.includes('punchOut') ? ['-'] : []),
            ...(activeFields.includes('scheduledHrs') ? [pcSched.toFixed(2)] : []),
            ...(activeFields.includes('actualHrs') ? [pcActual.toFixed(2)] : []),
            ...(isVarianceOtActive ? [pcVar.toFixed(2)] : []),
            ...(!isDaily && activeFields.includes('overtimeHrs') ? [pcOT.toFixed(2)] : []),
            ...(!isDaily && activeFields.includes('varianceHrs') ? [pcVar.toFixed(2)] : []),
            ...(activeFields.includes('otPercent') ? [pcOtPercent] : []),
            ...(activeFields.includes('otRisk') ? [pcOtRisk] : []),
          ];
          rows.push({
            isSummary: true,
            isGrand: false,
            data: pcTotalRow,
          });
        }

        corpSched += pcSched;
        corpActual += pcActual;
        corpOT += pcOT;
        corpVar += pcVar;
        if (pcMaxEmp > corpMaxEmp) corpMaxEmp = pcMaxEmp;
      });

      const corpOtPercent = corpActual > 0 ? `${((corpOT / corpActual) * 100).toFixed(2)}%` : '0.00%';
      const corpOtRisk = corpOT > corpActual * 0.1 ? 'High' : corpOT > 0 ? 'Medium' : 'Low';
      const corpTotalRow = [
        `${corpObj.corpName} Total`,
        ...(hasProfitCenter ? ['Grand Total'] : []),
        ...(reportType === 'monthly' && hasDepartment ? [''] : []),
        ...(reportType === 'monthly' && hasJob ? [''] : []),
        ...(reportType === 'weekly' && hasDepartment ? [''] : []),
        ...(reportType === 'weekly' && hasJob ? [''] : []),
        ...(reportType === 'weekly' ? ['', ''] : []),
        ...(reportType === 'monthly' ? [''] : []),
        ...(isDaily ? [''] : []),
        corpMaxEmp,
        ...(isDaily && activeFields.includes('punchIn') ? ['-'] : []),
        ...(isDaily && activeFields.includes('punchOut') ? ['-'] : []),
        ...(activeFields.includes('scheduledHrs') ? [corpSched.toFixed(2)] : []),
        ...(activeFields.includes('actualHrs') ? [corpActual.toFixed(2)] : []),
        ...(isVarianceOtActive ? [corpVar.toFixed(2)] : []),
        ...(!isDaily && activeFields.includes('overtimeHrs') ? [corpOT.toFixed(2)] : []),
        ...(!isDaily && activeFields.includes('varianceHrs') ? [corpVar.toFixed(2)] : []),
        ...(activeFields.includes('otPercent') ? [corpOtPercent] : []),
        ...(activeFields.includes('otRisk') ? [corpOtRisk] : []),
      ];
      rows.push({
        isSummary: true,
        isGrand: true,
        data: corpTotalRow,
      });

      allCorpsSched += corpSched;
      allCorpsActual += corpActual;
      allCorpsOT += corpOT;
      allCorpsVar += corpVar;
      allCorpsMaxEmp += corpMaxEmp;
    });

    if (corpMap.size > 1) {
      const allOtPercent =
        allCorpsActual > 0 ? `${((allCorpsOT / allCorpsActual) * 100).toFixed(2)}%` : '0.00%';
      const allOtRisk = allCorpsOT > allCorpsActual * 0.1 ? 'High' : allCorpsOT > 0 ? 'Medium' : 'Low';
      const allTotalRow = [
        'All Corporations Total',
        ...(hasProfitCenter ? ['Grand Total'] : []),
        ...(reportType === 'monthly' && hasDepartment ? [''] : []),
        ...(reportType === 'monthly' && hasJob ? [''] : []),
        ...(reportType === 'weekly' && hasDepartment ? [''] : []),
        ...(reportType === 'weekly' && hasJob ? [''] : []),
        ...(reportType === 'weekly' ? ['', ''] : []),
        ...(reportType === 'monthly' ? [''] : []),
        ...(isDaily ? [''] : []),
        allCorpsMaxEmp,
        ...(isDaily && activeFields.includes('punchIn') ? ['-'] : []),
        ...(isDaily && activeFields.includes('punchOut') ? ['-'] : []),
        ...(activeFields.includes('scheduledHrs') ? [allCorpsSched.toFixed(2)] : []),
        ...(activeFields.includes('actualHrs') ? [allCorpsActual.toFixed(2)] : []),
        ...(isVarianceOtActive ? [allCorpsVar.toFixed(2)] : []),
        ...(!isDaily && activeFields.includes('overtimeHrs') ? [allCorpsOT.toFixed(2)] : []),
        ...(!isDaily && activeFields.includes('varianceHrs') ? [allCorpsVar.toFixed(2)] : []),
        ...(activeFields.includes('otPercent') ? [allOtPercent] : []),
        ...(activeFields.includes('otRisk') ? [allOtRisk] : []),
      ];
      rows.push({
        isSummary: true,
        isGrand: true,
        data: allTotalRow,
      });
    }

    const tableBody = rows.map((r) => (r.data ? r.data : r));

    autoTable(doc, {
      startY: Math.max(currentY, DATA_TABLE_TOP_MARGIN),
      margin: {
        top: DATA_TABLE_TOP_MARGIN,
        left: PAGE_MARGIN,
        right: PAGE_MARGIN,
        bottom: PAGE_BOTTOM_MARGIN,
      },
      head: [headers],
      body: tableBody,
      theme: 'grid',
      headStyles: baseHeaderStyles,
      styles: baseBodyStyles,
      didParseCell: (hookData) => {
        if (hookData.section === 'body') {
          const rowObj = rows[hookData.row.index];
          if (rowObj && rowObj.isHeader) {
            hookData.cell.styles.fontStyle = 'bold';
            hookData.cell.styles.fillColor = [255, 255, 255];
            hookData.cell.styles.halign = 'center';
            return;
          }
          if (rowObj && rowObj.isSummary) {
            hookData.cell.styles.fontStyle = 'bold';
            hookData.cell.styles.fillColor = rowObj.isGrand ? [226, 232, 240] : [245, 245, 245];
          }

          // Numeric right align
          const header = headers[hookData.column.index];
          if (
            header === 'Scheduled Hrs' ||
            header === 'Actual Hrs' ||
            header === 'Overtime Hrs' ||
            header === 'Variance Hours' ||
            header === 'Variance Hours /OT Hrs' ||
            header === 'OT%' ||
            header === 'Employee Count'
          ) {
            hookData.cell.styles.halign = 'right';
          } else {
            hookData.cell.styles.halign = 'center';
          }
        }
      },
    });
  } else {
    // -------------------------------------------------------------
    // Single Corporation View (or Detailed Multi-Corp Daily)
    // -------------------------------------------------------------
    const isVarianceOtActive = isDaily && isMultiCorp && (activeFields.includes('varianceOvertimeHrs') || activeFields.includes('varianceHrs') || activeFields.includes('overtimeHrs'));

    const headers = [
      ...(isDaily && isMultiCorp ? ['Corporation'] : []),
      ...(isDaily && isMultiCorp && hasProfitCenter ? ['Profit Center'] : []),
      ...(isDaily && !isMultiCorp ? ['Date'] : []),
      'Employee Name',
      ...((reportType === 'weekly' && isMultiCorp) || (isDaily && isMultiCorp) ? ['Department', 'Job Title'] : []),
      ...(isDaily && activeFields.includes('punchIn') ? ['Punch In'] : []),
      ...(isDaily && activeFields.includes('punchOut') ? ['Punch Out'] : []),
      ...(activeFields.includes('scheduledHrs') ? ['Scheduled Hrs'] : []),
      ...(activeFields.includes('actualHrs') ? ['Actual Hrs'] : []),
      ...(isVarianceOtActive ? ['Variance Hours /OT Hrs'] : []),
      ...(!isVarianceOtActive && activeFields.includes('overtimeHrs') ? ['Overtime Hrs'] : []),
      ...(!isVarianceOtActive && activeFields.includes('varianceHrs') ? ['Variance Hours'] : []),
      ...(!isDaily && activeFields.includes('otPercent') ? ['OT %'] : []),
      ...(!isDaily && activeFields.includes('otRisk') ? ['OT Risk'] : []),
      ...(isDaily && activeFields.includes('status') ? ['Status'] : []),
    ];

    const groups =
      tableGroups && tableGroups.length > 0
        ? tableGroups
        : buildTableGroupsInternal(dataRecords, reportType, isMultiCorp, filters);

    let grandSched = 0;
    let grandActual = 0;
    let grandOt = 0;
    let grandVar = 0;

    groups.forEach((group) => {
      const groupRows = [];

      let groupSched = 0;
      let groupActual = 0;
      let groupOt = 0;
      let groupVar = 0;

      group.records.forEach((r) => {
        const sched = Number(r.scheduledHrs) || 0;
        const actual = Number(r.actualHrs) || 0;
        const ot = Number(r.overtimeHrs) || 0;
        const varH = Number(r.varianceHrs) || 0;

        groupSched += sched;
        groupActual += actual;
        groupOt += ot;
        groupVar += varH;

        const row = [
          ...(isDaily && isMultiCorp ? [r.corporationName || '-'] : []),
          ...(isDaily && isMultiCorp && hasProfitCenter ? [r.pcName || '-'] : []),
          ...(isDaily && !isMultiCorp ? [r.date || ''] : []),
          r.employeeName || '-',
          ...((reportType === 'weekly' && isMultiCorp) || (isDaily && isMultiCorp) ? [r.department || '-', r.jobTitle || '-'] : []),
          ...(isDaily && activeFields.includes('punchIn') ? [r.punchIn || '-'] : []),
          ...(isDaily && activeFields.includes('punchOut') ? [r.punchOut || '-'] : []),
          ...(activeFields.includes('scheduledHrs') ? [sched.toFixed(2)] : []),
          ...(activeFields.includes('actualHrs') ? [actual.toFixed(2)] : []),
          ...(isVarianceOtActive ? [varH.toFixed(2)] : []),
          ...(!isVarianceOtActive && activeFields.includes('overtimeHrs') ? [ot.toFixed(2)] : []),
          ...(!isVarianceOtActive && activeFields.includes('varianceHrs') ? [varH.toFixed(2)] : []),
          ...(!isDaily && activeFields.includes('otPercent') ? [r.otPercent || '0.00%'] : []),
          ...(!isDaily && activeFields.includes('otRisk') ? [r.otRisk || '-'] : []),
          ...(isDaily && activeFields.includes('status') ? [r.status || 'On Schedule'] : []),
        ];
        groupRows.push(row);
      });

      grandSched += groupSched;
      grandActual += groupActual;
      grandOt += groupOt;
      grandVar += groupVar;

      // Total Row with pre-computed values
      const otPStr =
        groupActual > 0 ? `${((groupOt / groupActual) * 100).toFixed(2)}%` : '0.00%';
      const otRStr = groupOt > 2 ? 'High' : groupOt > 0 ? 'Medium' : 'Low';

      const totalRow = [
        ...(isDaily && isMultiCorp ? [''] : []),
        ...(isDaily && isMultiCorp && hasProfitCenter ? [''] : []),
        ...(isDaily && !isMultiCorp ? [''] : []),
        'Total',
        ...((reportType === 'weekly' && isMultiCorp) || (isDaily && isMultiCorp) ? ['', ''] : []),
        ...(isDaily && activeFields.includes('punchIn') ? [''] : []),
        ...(isDaily && activeFields.includes('punchOut') ? [''] : []),
        ...(activeFields.includes('scheduledHrs') ? [groupSched.toFixed(2)] : []),
        ...(activeFields.includes('actualHrs') ? [groupActual.toFixed(2)] : []),
        ...(isVarianceOtActive ? [groupVar.toFixed(2)] : []),
        ...(!isVarianceOtActive && activeFields.includes('overtimeHrs') ? [groupOt.toFixed(2)] : []),
        ...(!isVarianceOtActive && activeFields.includes('varianceHrs') ? [groupVar.toFixed(2)] : []),
        ...(!isDaily && activeFields.includes('otPercent') ? [otPStr] : []),
        ...(!isDaily && activeFields.includes('otRisk') ? [otRStr] : []),
        ...(isDaily && activeFields.includes('status') ? [''] : []),
      ];

      // Build table head: Subheader with Department & Job + Column Headers
      // Placing the Subheader into head ensures jspdf-autotable automatically repeats it on every page where the table continues!
      const tableHead = [];
      if (group.headerTitle) {
        tableHead.push([
          {
            content: group.headerTitle,
            colSpan: headers.length,
            styles: {
              fillColor: isDaily && isMultiCorp ? [255, 255, 255] : [241, 245, 249],
              textColor: [15, 23, 42],
              fontStyle: 'bold',
              fontSize: 8.5,
              halign: isDaily && isMultiCorp ? 'center' : 'left',
              cellPadding: 3,
            },
          },
        ]);
      }
      tableHead.push(headers);

      if (currentY + 25 > pageHeight - PAGE_BOTTOM_MARGIN) {
        doc.addPage();
        currentY = DATA_TABLE_TOP_MARGIN;
      }

      autoTable(doc, {
        startY: Math.max(currentY, DATA_TABLE_TOP_MARGIN),
        margin: {
          top: DATA_TABLE_TOP_MARGIN,
          left: PAGE_MARGIN,
          right: PAGE_MARGIN,
          bottom: PAGE_BOTTOM_MARGIN,
        },
        head: tableHead,
        body: [...groupRows, totalRow],
        theme: 'grid',
        headStyles: baseHeaderStyles,
        styles: baseBodyStyles,
        didParseCell: (hookData) => {
          if (hookData.section === 'head' && hookData.row.index === 0 && group.headerTitle) {
            hookData.cell.styles.fillColor = isDaily && isMultiCorp ? [255, 255, 255] : [241, 245, 249];
            hookData.cell.styles.textColor = [15, 23, 42];
            hookData.cell.styles.fontStyle = 'bold';
            hookData.cell.styles.fontSize = 8.5;
            hookData.cell.styles.halign = isDaily && isMultiCorp ? 'center' : 'left';
          } else if (hookData.section === 'body') {
            const isLastRow = hookData.row.index === groupRows.length;
            if (isLastRow) {
              hookData.cell.styles.fontStyle = 'bold';
              hookData.cell.styles.fillColor = [245, 245, 245];
            }

            const header = headers[hookData.column.index];
            if (
              header === 'Scheduled Hrs' ||
              header === 'Actual Hrs' ||
              header === 'Overtime Hrs' ||
              header === 'Variance Hours' ||
              header === 'Variance Hours /OT Hrs' ||
              header === 'OT %'
            ) {
              hookData.cell.styles.halign = 'right';
            } else if (header === 'Employee Name' || header === 'Corporation') {
              hookData.cell.styles.halign = 'left';
            } else {
              hookData.cell.styles.halign = 'center';
            }
          }
        },
      });

      currentY = doc.lastAutoTable.finalY + 6;
    });

    // Grand Total Row at the very end when multiple groups exist
    if (groups.length > 1) {
      const grandOtPStr =
        grandActual > 0 ? `${((grandOt / grandActual) * 100).toFixed(2)}%` : '0.00%';
      const grandOtRStr = grandOt > grandActual * 0.1 ? 'High' : grandOt > 0 ? 'Medium' : 'Low';

      const grandTotalRow = [
        ...(isDaily && isMultiCorp ? [''] : []),
        ...(isDaily && isMultiCorp && hasProfitCenter ? [''] : []),
        ...(isDaily && !isMultiCorp ? [''] : []),
        'Grand Total',
        ...(reportType === 'weekly' || (isDaily && isMultiCorp) ? ['', ''] : []),
        ...(isDaily && activeFields.includes('punchIn') ? [''] : []),
        ...(isDaily && activeFields.includes('punchOut') ? [''] : []),
        ...(activeFields.includes('scheduledHrs') ? [grandSched.toFixed(2)] : []),
        ...(activeFields.includes('actualHrs') ? [grandActual.toFixed(2)] : []),
        ...(isVarianceOtActive ? [grandVar.toFixed(2)] : []),
        ...(!isVarianceOtActive && activeFields.includes('overtimeHrs') ? [grandOt.toFixed(2)] : []),
        ...(!isVarianceOtActive && activeFields.includes('varianceHrs') ? [grandVar.toFixed(2)] : []),
        ...(!isDaily && activeFields.includes('otPercent') ? [grandOtPStr] : []),
        ...(!isDaily && activeFields.includes('otRisk') ? [grandOtRStr] : []),
        ...(isDaily && activeFields.includes('status') ? [''] : []),
      ];

      if (currentY + 20 > pageHeight - PAGE_BOTTOM_MARGIN) {
        doc.addPage();
        currentY = DATA_TABLE_TOP_MARGIN;
      }

      autoTable(doc, {
        startY: Math.max(currentY, DATA_TABLE_TOP_MARGIN),
        margin: {
          top: DATA_TABLE_TOP_MARGIN,
          left: PAGE_MARGIN,
          right: PAGE_MARGIN,
          bottom: PAGE_BOTTOM_MARGIN,
        },
        body: [grandTotalRow],
        theme: 'grid',
        styles: baseBodyStyles,
        didParseCell: (hookData) => {
          hookData.cell.styles.fontStyle = 'bold';
          hookData.cell.styles.fillColor = [245, 245, 245];
          const header = headers[hookData.column.index];
          if (
            header === 'Scheduled Hrs' ||
            header === 'Actual Hrs' ||
            header === 'Overtime Hrs' ||
            header === 'Variance Hours' ||
            header === 'OT %'
          ) {
            hookData.cell.styles.halign = 'right';
          } else if (header === 'Employee Name' || header === 'Corporation') {
            hookData.cell.styles.halign = 'left';
          } else {
            hookData.cell.styles.halign = 'center';
          }
        },
      });

      currentY = doc.lastAutoTable.finalY + 6;
    }
  }

  // -------------------------------------------------------------
  // 3. Render Header Box Table & Page Numbers on EVERY page
  // -------------------------------------------------------------
  const totalPages = doc.internal.getNumberOfPages();
  const boxData = {
    isMultiCorp,
    reportTypeFormatted,
    mainTitle,
    finalCorpLabel,
    dateRangeStr,
    finalPcLabel,
    finalBusinessLabel,
    finalPcNoLabel,
    PAGE_MARGIN,
    pageWidth,
  };

  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    // Draw Header Box Table at the top of every page
    drawHeaderBox(doc, boxData);

    // Draw Footer Page Number at the bottom of every page
    doc.setFont('Poppins', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(120, 120, 120);
    doc.text(`Page ${i} of ${totalPages}`, pageWidth - PAGE_MARGIN, pageHeight - 6, {
      align: 'right',
    });
  }

  // Download PDF
  const filename = `Time_And_Attendance_Report_${reportTypeFormatted}.pdf`;
  doc.save(filename);
};
